import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper, CircularProgress, Alert, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getSiteSectionBySlug } from '../services/api';

const ArticlePage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                const data = await getSiteSectionBySlug(slug);
                setArticle(data);
            } catch (err) {
                console.error("Failed to load article:", err);
                setError('Artigo não encontrado ou erro ao carregar.');
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [slug]);

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !article) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error || 'Artigo não encontrado.'}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} variant="contained">
                    Voltar ao Início
                </Button>
            </Box>
        );
    }

    // Extract content fields
    const { title, description, content, createdAt, updatedAt } = article;
    const author = content?.author || 'Equipe R5';
    const image = content?.image || '';
    const label = content?.label || 'Artigo';

    const publishDate = new Date(updatedAt || createdAt).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    return (
        <Box sx={{ backgroundColor: '#fafafa', minHeight: '100vh', pb: 8 }}>
            
            {/* Nav Bar Placeholder or Back Button */}
            <Box sx={{ backgroundColor: '#fff', borderBottom: '1px solid #eee', py: 2 }}>
                <Container maxWidth="lg">
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/')}
                        sx={{ color: '#333', textTransform: 'none', fontWeight: 600 }}
                    >
                        Voltar para Início
                    </Button>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ mt: 6 }}>
                <Paper 
                    elevation={0}
                    sx={{ 
                        borderRadius: '24px', 
                        overflow: 'hidden', 
                        border: '1px solid rgba(0,0,0,0.08)',
                        backgroundColor: '#fff'
                    }}
                >
                    {/* Hero Image */}
                    {image && (
                        <Box 
                            component="img" 
                            src={image} 
                            alt={title}
                            sx={{ 
                                width: '100%', 
                                height: { xs: 250, md: 400 }, 
                                objectFit: 'cover' 
                            }} 
                        />
                    )}

                    <Box sx={{ p: { xs: 3, md: 6 } }}>
                        {/* Meta Tags */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                             <Chip 
                                label={label} 
                                sx={{ 
                                    backgroundColor: '#CC0000', 
                                    color: '#fff', 
                                    fontWeight: 700, 
                                    fontSize: '0.75rem',
                                    height: '24px'
                                }} 
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#666' }}>
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>{publishDate}</Typography>
                            </Box>
                        </Box>

                        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 3, color: '#1a1a1a', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                            {title}
                        </Typography>

                        {/* Author */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, pb: 4, borderBottom: '1px solid #f0f0f0' }}>
                            <Box sx={{ 
                                width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f5f5f5', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <PersonIcon sx={{ color: '#999' }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>
                                    {author}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                    Autor
                                </Typography>
                            </Box>
                        </Box>

                        {/* Content Body */}
                        <Box 
                            sx={{ 
                                '& p': { fontSize: '1.1rem', lineHeight: 1.7, color: '#444', mb: 2 },
                                '& h2': { fontSize: '1.8rem', fontWeight: 700, mt: 4, mb: 2, color: '#1a1a1a' },
                                '& h3': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 2, color: '#2a2a2a' },
                                '& ul, & ol': { mb: 3, pl: 4 },
                                '& li': { mb: 1, fontSize: '1.1rem', color: '#444' },
                                '& blockquote': { borderLeft: '4px solid #CC0000', pl: 2, my: 3, fontStyle: 'italic', color: '#555' }
                            }}
                            dangerouslySetInnerHTML={{ __html: article.body || description }} 
                        />

                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ArticlePage;
